import {type ChangeEvent, type FocusEvent, type FormEvent, type InputHTMLAttributes, useEffect, useId, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {
    type ISearchComboboxNavItem,
    SearchComboboxDropdown,
    SearchComboboxFieldControls,
    SearchComboboxLiveRegion,
    type SearchComboboxMode,
    type SearchComboboxOpenChangeReason,
    useAnchoredPanel,
    useSearchCombobox,
    useSearchComboboxMode
} from "@pg-design/search-combobox-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";
import {PRICES_CITIES} from "@pg-rp/navigation";

import type {ISearchInputRegion} from "../../../../search/components/ISearchInputValue";
import {scheduleTrackingTask} from "../../../../tracking/utils/schedule_tracking_task";
import {PRICES_SEARCH_COPY, PRICES_SEARCH_LAYER_MAX_WIDTH_PX} from "../../constants/prices_search";
import {usePricesRecentSearches} from "../../hooks/use_prices_recent_searches";
import {usePricesSearchLayerHistory} from "../../hooks/use_prices_search_layer_history";
import {usePricesSearchNearby} from "../../hooks/use_prices_search_nearby";
import {usePricesSearchSuggestions} from "../../hooks/use_prices_search_suggestions";
import {type PricesSearchAnalyticsLocation, type PricesSearchMethod, trackPricesSearch} from "../../tracking/track_prices_search";
import type {
    IPricesRecentSearch,
    IPricesSearchContext,
    IPricesSearchModel,
    IPricesSearchNearbyTarget,
    PricesSearchNavigableItem
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../../types/IRcnAddress";
import {getAddressLink} from "../../utils/get_address_link";
import {formatRcnAddressLabel, formatRcnCityName} from "../../utils/prices_search/format_rcn_display_name";
import {buildPricesRecentCitySearch, buildPricesRecentDistrictSearch} from "../../utils/prices_search/map_main_recent_to_prices";
import {
    addPricesRecentSearch,
    getPricesRecentAddressId,
    getPricesRecentDistrictId,
    savePricesDistrictToMainSearch,
    writePricesSearchMirrorMarker
} from "../../utils/prices_search/prices_recent_searches_storage";
import {PricesSearchField} from "./PricesSearchField";
import {PricesSearchLayer} from "./PricesSearchLayer";
import {PricesSearchResults} from "./PricesSearchResults";

const COPY = PRICES_SEARCH_COPY;
const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

interface IProps {
    /** Analytics `location` of the page the search is on. */
    location: PricesSearchAnalyticsLocation;
    context?: IPricesSearchContext;
    className?: string;
    dataTestId?: string;
}

interface IDistrictSelection {
    regionId: number;
    citySlug: string;
    title: string;
    href: string;
    region?: ISearchInputRegion;
}

// Row ids carry their section: recent rows, popular rows and the empty-state chips report their own search method.
const getSearchMethod = (itemId: string): PricesSearchMethod => {
    if (itemId.startsWith("recent:")) {
        return "recent_search_click";
    }

    return itemId.startsWith("popular:") || itemId.startsWith("empty:") ? "popular_search_click" : "manual_input";
};

const saveCitySearch = (citySlug: string, regionId: number | null) => {
    const entry = buildPricesRecentCitySearch(citySlug, Date.now(), regionId);
    if (entry) {
        addPricesRecentSearch(entry);
    }
    // The main search store gets the city from its landing page, which can fetch the full region object.
    if (regionId !== null) {
        writePricesSearchMirrorMarker(regionId);
    }
};

const saveDistrictSearch = (district: IDistrictSelection) => {
    const cityName = PRICES_CITY_NAME_BY_SLUG.get(district.citySlug) ?? "";
    const entry = district.region ? buildPricesRecentDistrictSearch(district.region, district.citySlug, Date.now()) : null;

    addPricesRecentSearch(
        entry ?? {
            id: getPricesRecentDistrictId(district.regionId),
            kind: "district",
            title: district.title,
            subtitle: `${COPY.subtitles.district}${COPY.subtitleSeparator}${cityName}`,
            href: district.href,
            citySlug: district.citySlug,
            regionId: district.regionId
        }
    );
    // With the full region at hand it goes to the main store now; otherwise the district page mirrors it.
    if (!district.region || !savePricesDistrictToMainSearch(district.region)) {
        writePricesSearchMirrorMarker(district.regionId);
    }
};

const saveAddressSearch = (rcn: IRcnAddressKey, citySlug: string) => {
    addPricesRecentSearch({
        id: getPricesRecentAddressId(rcn),
        kind: "address",
        title: formatRcnAddressLabel(rcn),
        subtitle: `${COPY.subtitles.address}${COPY.subtitleSeparator}${formatRcnCityName(rcn.city)}`,
        href: getAddressLink(rcn),
        citySlug,
        rcn
    });
};

// A recent row picked again moves to the top; cities and districts are mirrored like a fresh pick.
const saveRecentAgain = (entry: IPricesRecentSearch) => {
    addPricesRecentSearch({...entry, timestamp: undefined});
    if ((entry.kind === "city" || entry.kind === "district") && entry.regionId !== undefined) {
        writePricesSearchMirrorMarker(entry.regionId);
    }
};

const getLiveMessage = (model: IPricesSearchModel): string => {
    switch (model.state) {
        case "loading":
            return COPY.loading;
        case "error":
            return COPY.live.error;
        case "empty":
            return model.empty?.variant === "cityWithoutData" && model.empty.cityName
                ? COPY.cityWithoutDataTitle(model.empty.cityName)
                : COPY.noResultsTitle(model.empty?.query ?? "");
        case "results": {
            const count = model.sections.reduce(
                (sum, section) => sum + section.items.reduce((rows, item) => rows + 1 + (item.kind === "street" ? (item.numbers?.items.length ?? 0) : 0), 0),
                0
            );
            const results = COPY.live.results(count);

            return model.banner?.kind === "diacritics" ? `${COPY.diacriticsBanner} „${model.banner.phrase}”. ${results}` : results;
        }
        default:
            return "";
    }
};

/**
 * Prices search: the hero field with the suggestions dropdown on desktop and the full-screen layer
 * below 1024px. One component tree for both: the mode is read from the viewport at the moment the user opens the
 * search, never from `useUserDevice`, so hydration or a resize never remounts the field and loses what was typed.
 * Picking a location navigates right away and saves it to the recent searches.
 */
export const PricesSearch = (props: IProps) => {
    const {location, className, dataTestId} = props;
    const {citySlug, districtRegionId} = props.context ?? {};
    const context = useMemo((): IPricesSearchContext => ({citySlug, districtRegionId}), [citySlug, districtRegionId]);

    const formRef = useRef<HTMLFormElement>(null);
    const heroInputRef = useRef<HTMLInputElement>(null);
    const layerReturnFocusRef = useRef<HTMLElement | null>(null);
    const layerInputRef = useRef<HTMLInputElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);
    const heroInputId = useId();

    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<SearchComboboxMode>("dropdown");
    const isLayerOpen = isOpen && mode === "layer";
    const isDropdownOpen = isOpen && mode === "dropdown";

    const recentSearches = usePricesRecentSearches();
    const nearby = usePricesSearchNearby({onResolved: (target) => selectNearbyTarget(target)});
    // Closing the panel abandons a pending "Ceny w mojej okolicy" attempt: a late answer must not navigate on its own.
    const {reset: resetNearby} = nearby;
    useEffect(() => {
        if (!isOpen) {
            resetNearby();
        }
    }, [isOpen, resetNearby]);
    const navItemsRef = useRef<ISearchComboboxNavItem[]>([]);
    const suggestions = usePricesSearchSuggestions({
        query,
        isOpen,
        context,
        recent: recentSearches.items,
        nearby: nearby.state,
        onPendingSubmitReady: (itemId) => navItemsRef.current.find((item) => item.id === itemId)?.onSelect()
    });
    const {model} = suggestions;

    const layerHistory = usePricesSearchLayerHistory({isOpen: isLayerOpen, onClose: () => setIsOpen(false)});
    const closePanel = () => (mode === "layer" ? layerHistory.close() : setIsOpen(false));

    const {resolveMode, isLayerViewport} = useSearchComboboxMode({
        layerMaxWidthPx: PRICES_SEARCH_LAYER_MAX_WIDTH_PX,
        // Crossing the breakpoint with the panel open closes it; the phrase stays for the next open.
        onViewportModeChange: () => {
            if (isOpen) {
                closePanel();
            }
        }
    });

    const panel = useAnchoredPanel({anchorRef: formRef, isOpen: isDropdownOpen, mode: "portal"});
    const rootRefs = useMemo(() => [formRef, panel.panelRef, layerRef], [panel.panelRef]);

    const track = (searchQuery: string, searchMethod: PricesSearchMethod) =>
        scheduleTrackingTask(() => trackPricesSearch({location, searchQuery, searchMethod}));

    // iOS raises the keyboard only for a focus() made while the tap is handled: the layer mounts synchronously here
    // and focuses its input in the same commit.
    const openLayer = () => {
        // Closing returns focus to the control that opened the layer ("Szukaj" or the hero field). Safari does not focus a
        // tapped button, so the hero field is the fallback.
        const {activeElement} = document;
        layerReturnFocusRef.current = activeElement instanceof HTMLElement && formRef.current?.contains(activeElement) ? activeElement : heroInputRef.current;
        flushSync(() => {
            setMode("layer");
            setIsOpen(true);
        });
    };

    const openPanel = (reason: SearchComboboxOpenChangeReason) => {
        if (resolveMode() === "layer") {
            // Focus may come back to the hero field when the layer closes - it must not open it again.
            if (reason !== "focus") {
                openLayer();
            }
            return;
        }

        setMode("dropdown");
        setIsOpen(true);
    };

    const onOpenChange = (nextIsOpen: boolean, reason: SearchComboboxOpenChangeReason) => {
        if (nextIsOpen) {
            if (!isOpen) {
                openPanel(reason);
            }
            return;
        }

        if (mode === "dropdown") {
            setIsOpen(false);
            return;
        }

        // Esc typed in the layer input is stopped by the combobox hook, so the layer's own Esc handling never sees it:
        // close through the history entry like ←. Tab and outside presses stay inside the full-screen layer.
        if (reason === "escape") {
            layerHistory.close();
        }
    };

    const clearQuery = () => {
        suggestions.abortAll();
        setQuery("");
    };

    function selectNearbyTarget(target: IPricesSearchNearbyTarget) {
        if (target.kind === "city") {
            saveCitySearch(target.citySlug, target.regionId);
        } else {
            saveDistrictSearch(target);
        }
        track(target.title, "manual_input");
        layerHistory.leave(target.href);
    }

    const selectLocation = (item: PricesSearchNavigableItem) => {
        // Another row picked while "Ceny w mojej okolicy" is locating: its late answer must not navigate over this one.
        resetNearby();
        const recentEntry = "recentId" in item && item.recentId ? recentSearches.items.find((entry) => entry.id === item.recentId) : undefined;

        switch (item.kind) {
            case "city":
            case "chip":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveCitySearch(item.citySlug, item.regionId);
                }
                break;
            case "district":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveDistrictSearch({regionId: item.regionId, citySlug: item.citySlug, title: item.title, href: item.href ?? "", region: item.region});
                }
                break;
            case "address":
            case "number":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveAddressSearch(item.rcn, item.citySlug);
                }
                break;
            default:
                return;
        }

        if (item.href) {
            track(item.kind === "chip" ? item.label : item.searchLabel, getSearchMethod(item.id));
            layerHistory.leave(item.href);
        }
    };

    const selectItem = (item: PricesSearchNavigableItem) => {
        switch (item.kind) {
            case "street":
                suggestions.toggleStreet(item);
                return;
            case "showMore":
                suggestions.showMore(item);
                return;
            case "clearRecent":
                recentSearches.clear();
                return;
            case "retry":
                suggestions.retry();
                return;
            case "nearby":
                if (item.state.status === "resolved") {
                    selectNearbyTarget(item.state.target);
                } else {
                    nearby.locate();
                }
                return;
            default:
                // Enter on the auto-active number whose price is still loading (the combobox selects it directly, not through
                // onSubmitWithoutActive): wait for the price - an unavailable one hands Enter to the next row.
                if (item.id === model.autoActiveId && suggestions.isAutoActiveResolving) {
                    suggestions.markPendingSubmit();
                    return;
                }
                selectLocation(item);
        }
    };

    // Rows keep stable handlers; a selection always runs with the state of the last render. Assigned while rendering, as
    // a pending Enter may select from an effect of the suggestions hook, which runs before this component's effects.
    const selectItemRef = useRef(selectItem);
    selectItemRef.current = selectItem;

    const navItems = useMemo(
        () =>
            model.navigation.map(
                (item): ISearchComboboxNavItem => ({
                    id: item.id,
                    onSelect: () => selectItemRef.current(item),
                    isNavigation: item.isNavigation,
                    ...("recentId" in item && item.recentId ? {onRemove: () => recentSearches.remove(item.recentId as string)} : {}),
                    ...(item.kind === "street" ? {isExpanded: item.isExpanded} : {}),
                    // No data behind it: the address page would only redirect to `?adres_niedostepny=1`.
                    ...((item.kind === "number" || item.kind === "address") && item.isUnavailable ? {isDisabled: true} : {}),
                    // The browser will not ask for the location again, so selecting the row could do nothing.
                    ...(item.kind === "nearby" && item.state.status === "blocked" ? {isDisabled: true} : {})
                })
            ),
        [model, recentSearches.remove]
    );
    navItemsRef.current = navItems;

    // Enter or "Szukaj" with nothing active.
    const onSubmitWithoutActive = () => {
        if (!isOpen) {
            if (resolveMode() === "layer") {
                // The lime "Szukaj" under the field on mobile opens the layer with the current phrase.
                openLayer();
                return;
            }

            openPanel("submit");
            if (suggestions.parsed) {
                suggestions.markPendingSubmit();
            } else {
                heroInputRef.current?.focus();
            }
            return;
        }

        if (!suggestions.parsed) {
            (mode === "layer" ? layerInputRef : heroInputRef).current?.focus();
            return;
        }
        if (model.state === "error") {
            suggestions.retry();
            return;
        }
        if (model.state === "results" && model.autoActiveId !== null) {
            navItems.find((item) => item.id === model.autoActiveId)?.onSelect();
            return;
        }
        if (suggestions.isPending) {
            suggestions.markPendingSubmit();
        }
    };

    const combobox = useSearchCombobox({
        isOpen,
        onOpenChange,
        items: navItems,
        autoActiveId: model.autoActiveId,
        inputValue: query,
        onSubmitWithoutActive,
        rootRefs,
        listboxLabel: COPY.listboxLabel
    });

    // "Pokaż więcej" and a street expansion hand the keyboard position to the first newly shown row.
    const {requestedActiveId, consumeActiveRequest} = suggestions;
    const {setActiveId} = combobox;
    useEffect(() => {
        if (requestedActiveId !== null) {
            setActiveId(requestedActiveId);
            consumeActiveRequest();
        }
    }, [requestedActiveId, setActiveId, consumeActiveRequest]);

    const onClear = () => {
        clearQuery();
        combobox.reset();
        (mode === "layer" ? layerInputRef : heroInputRef).current?.focus();
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        combobox.submit();
    };

    const inputUserProps = {
        value: query,
        onChange: (event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value),
        placeholder: COPY.placeholder
    };

    // Below 1024px the hero field is only the trigger of the layer: no keyboard, announced as opening a dialog.
    const heroTriggerProps = {
        readOnly: isLayerViewport,
        "aria-haspopup": isLayerViewport ? ("dialog" as const) : undefined,
        // Below 1024px focus alone must not reach the combobox: ignoring its open request in `onOpenChange` would leave
        // the request pending, and a tap delivers focus and click in one task - the click that opens the layer would be
        // dropped. Cancelling here skips the hook's focus handling altogether.
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
            if (resolveMode() === "layer") {
                event.preventDefault();
            }
        }
    };
    const heroInputProps: InputHTMLAttributes<HTMLInputElement> & {id: string} = isLayerOpen
        ? {...inputUserProps, ...heroTriggerProps, id: heroInputId, autoComplete: "off"}
        : combobox.getInputProps({...inputUserProps, ...heroTriggerProps});
    const layerInputProps = isLayerOpen
        ? ({...combobox.getInputProps(inputUserProps), "data-testid": PRICES_SEARCH_TEST_IDS.MOBILE_INPUT} as InputHTMLAttributes<HTMLInputElement>)
        : {};

    const liveMessage = isOpen ? getLiveMessage(model) : "";
    const listProps = {
        combobox,
        onRemoveRecent: recentSearches.remove
    };

    return (
        <form ref={formRef} role="search" className={cx(formStyle, className)} onSubmit={onSubmit} data-testid={dataTestId}>
            <PricesSearchField
                inputProps={heroInputProps}
                inputRef={heroInputRef}
                label={COPY.inputLabel}
                inputDataTestId={PRICES_SEARCH_TEST_IDS.INPUT}
                controls={
                    // The mobile trigger has no controls - they live in the layer field.
                    isLayerViewport ? null : (
                        <SearchComboboxFieldControls
                            isBusy={isDropdownOpen && suggestions.isBusy}
                            showClear={query.length > 0}
                            onClear={onClear}
                            clearLabel={COPY.clearField}
                            clearDataTestId={PRICES_SEARCH_TEST_IDS.CLEAR}
                            spinnerDataTestId={PRICES_SEARCH_TEST_IDS.SPINNER}
                        />
                    )
                }
            />

            <Button type="submit" variant="filled_primary" size="big" className={buttonStyle} dataTestId={PRICES_SEARCH_TEST_IDS.SUBMIT}>
                Szukaj
            </Button>

            <SearchComboboxLiveRegion message={isLayerOpen ? "" : liveMessage} />

            <SearchComboboxDropdown
                isOpen={isDropdownOpen}
                panel={panel}
                listboxProps={combobox.getListboxProps()}
                dataTestId={PRICES_SEARCH_TEST_IDS.DROPDOWN}
            >
                <PricesSearchResults model={model} mode="dropdown" {...listProps} />
            </SearchComboboxDropdown>

            <PricesSearchLayer
                isOpen={isLayerOpen}
                onClose={layerHistory.close}
                inputProps={layerInputProps}
                inputRef={layerInputRef}
                returnFocusRef={layerReturnFocusRef}
                layerRef={layerRef}
                listboxProps={combobox.getListboxProps()}
                controls={
                    <SearchComboboxFieldControls
                        size="mobile"
                        isBusy={suggestions.isBusy}
                        showClear={query.length > 0}
                        onClear={onClear}
                        clearLabel={COPY.clearField}
                        clearDataTestId={PRICES_SEARCH_TEST_IDS.CLEAR}
                        spinnerDataTestId={PRICES_SEARCH_TEST_IDS.SPINNER}
                    />
                }
                liveRegion={<SearchComboboxLiveRegion message={isLayerOpen ? liveMessage : ""} />}
            >
                <PricesSearchResults model={model} mode="layer" {...listProps} />
            </PricesSearchLayer>
        </form>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",

    md: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 2
    }
});

const buttonStyle = css({
    flex: "0 0 auto",
    width: "100%",
    height: "5.6rem",

    md: {
        width: "20rem",
        height: "7.2rem"
    }
});
import {type ChangeEvent, type FocusEvent, type FormEvent, type InputHTMLAttributes, useEffect, useId, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {
    type ISearchComboboxNavItem,
    SearchComboboxDropdown,
    SearchComboboxFieldControls,
    SearchComboboxLiveRegion,
    type SearchComboboxMode,
    type SearchComboboxOpenChangeReason,
    useAnchoredPanel,
    useSearchCombobox,
    useSearchComboboxMode
} from "@pg-design/search-combobox-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";
import {PRICES_CITIES} from "@pg-rp/navigation";

import type {ISearchInputRegion} from "../../../../search/components/ISearchInputValue";
import {scheduleTrackingTask} from "../../../../tracking/utils/schedule_tracking_task";
import {PRICES_SEARCH_COPY, PRICES_SEARCH_LAYER_MAX_WIDTH_PX} from "../../constants/prices_search";
import {usePricesRecentSearches} from "../../hooks/use_prices_recent_searches";
import {usePricesSearchLayerHistory} from "../../hooks/use_prices_search_layer_history";
import {usePricesSearchNearby} from "../../hooks/use_prices_search_nearby";
import {usePricesSearchSuggestions} from "../../hooks/use_prices_search_suggestions";
import {type PricesSearchAnalyticsLocation, type PricesSearchMethod, trackPricesSearch} from "../../tracking/track_prices_search";
import type {
    IPricesRecentSearch,
    IPricesSearchContext,
    IPricesSearchModel,
    IPricesSearchNearbyTarget,
    PricesSearchNavigableItem
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../../types/IRcnAddress";
import {getAddressLink} from "../../utils/get_address_link";
import {formatRcnAddressLabel, formatRcnCityName} from "../../utils/prices_search/format_rcn_display_name";
import {buildPricesRecentCitySearch, buildPricesRecentDistrictSearch} from "../../utils/prices_search/map_main_recent_to_prices";
import {
    addPricesRecentSearch,
    getPricesRecentAddressId,
    getPricesRecentDistrictId,
    savePricesDistrictToMainSearch,
    writePricesSearchMirrorMarker
} from "../../utils/prices_search/prices_recent_searches_storage";
import {PricesSearchField} from "./PricesSearchField";
import {PricesSearchLayer} from "./PricesSearchLayer";
import {PricesSearchResults} from "./PricesSearchResults";

const COPY = PRICES_SEARCH_COPY;
const PRICES_CITY_NAME_BY_SLUG = new Map<string, string>(PRICES_CITIES.map((city) => [city.slug, city.name]));

interface IProps {
    /** Analytics `location` of the page the search is on. */
    location: PricesSearchAnalyticsLocation;
    context?: IPricesSearchContext;
    className?: string;
    dataTestId?: string;
}

interface IDistrictSelection {
    regionId: number;
    citySlug: string;
    title: string;
    href: string;
    region?: ISearchInputRegion;
}

// Row ids carry their section: recent rows, popular rows and the empty-state chips report their own search method.
const getSearchMethod = (itemId: string): PricesSearchMethod => {
    if (itemId.startsWith("recent:")) {
        return "recent_search_click";
    }

    return itemId.startsWith("popular:") || itemId.startsWith("empty:") ? "popular_search_click" : "manual_input";
};

const saveCitySearch = (citySlug: string, regionId: number | null) => {
    const entry = buildPricesRecentCitySearch(citySlug, Date.now(), regionId);
    if (entry) {
        addPricesRecentSearch(entry);
    }
    // The main search store gets the city from its landing page, which can fetch the full region object.
    if (regionId !== null) {
        writePricesSearchMirrorMarker(regionId);
    }
};

const saveDistrictSearch = (district: IDistrictSelection) => {
    const cityName = PRICES_CITY_NAME_BY_SLUG.get(district.citySlug) ?? "";
    const entry = district.region ? buildPricesRecentDistrictSearch(district.region, district.citySlug, Date.now()) : null;

    addPricesRecentSearch(
        entry ?? {
            id: getPricesRecentDistrictId(district.regionId),
            kind: "district",
            title: district.title,
            subtitle: `${COPY.subtitles.district}${COPY.subtitleSeparator}${cityName}`,
            href: district.href,
            citySlug: district.citySlug,
            regionId: district.regionId
        }
    );
    // With the full region at hand it goes to the main store now; otherwise the district page mirrors it.
    if (!district.region || !savePricesDistrictToMainSearch(district.region)) {
        writePricesSearchMirrorMarker(district.regionId);
    }
};

const saveAddressSearch = (rcn: IRcnAddressKey, citySlug: string) => {
    addPricesRecentSearch({
        id: getPricesRecentAddressId(rcn),
        kind: "address",
        title: formatRcnAddressLabel(rcn),
        subtitle: `${COPY.subtitles.address}${COPY.subtitleSeparator}${formatRcnCityName(rcn.city)}`,
        href: getAddressLink(rcn),
        citySlug,
        rcn
    });
};

// A recent row picked again moves to the top; cities and districts are mirrored like a fresh pick.
const saveRecentAgain = (entry: IPricesRecentSearch) => {
    addPricesRecentSearch({...entry, timestamp: undefined});
    if ((entry.kind === "city" || entry.kind === "district") && entry.regionId !== undefined) {
        writePricesSearchMirrorMarker(entry.regionId);
    }
};

const getLiveMessage = (model: IPricesSearchModel): string => {
    switch (model.state) {
        case "loading":
            return COPY.loading;
        case "error":
            return COPY.live.error;
        case "empty":
            return model.empty?.variant === "cityWithoutData" && model.empty.cityName
                ? COPY.cityWithoutDataTitle(model.empty.cityName)
                : COPY.noResultsTitle(model.empty?.query ?? "");
        case "results": {
            const count = model.sections.reduce(
                (sum, section) => sum + section.items.reduce((rows, item) => rows + 1 + (item.kind === "street" ? (item.numbers?.items.length ?? 0) : 0), 0),
                0
            );
            const results = COPY.live.results(count);

            return model.banner?.kind === "diacritics" ? `${COPY.diacriticsBanner} „${model.banner.phrase}”. ${results}` : results;
        }
        default:
            return "";
    }
};

/**
 * Prices search: the hero field with the suggestions dropdown on desktop and the full-screen layer
 * below 1024px. One component tree for both: the mode is read from the viewport at the moment the user opens the
 * search, never from `useUserDevice`, so hydration or a resize never remounts the field and loses what was typed.
 * Picking a location navigates right away and saves it to the recent searches.
 */
export const PricesSearch = (props: IProps) => {
    const {location, className, dataTestId} = props;
    const {citySlug, districtRegionId} = props.context ?? {};
    const context = useMemo((): IPricesSearchContext => ({citySlug, districtRegionId}), [citySlug, districtRegionId]);

    const formRef = useRef<HTMLFormElement>(null);
    const heroInputRef = useRef<HTMLInputElement>(null);
    const layerReturnFocusRef = useRef<HTMLElement | null>(null);
    const layerInputRef = useRef<HTMLInputElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);
    const heroInputId = useId();

    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<SearchComboboxMode>("dropdown");
    const isLayerOpen = isOpen && mode === "layer";
    const isDropdownOpen = isOpen && mode === "dropdown";

    const recentSearches = usePricesRecentSearches();
    const nearby = usePricesSearchNearby({onResolved: (target) => selectNearbyTarget(target)});
    // Closing the panel abandons a pending "Ceny w mojej okolicy" attempt: a late answer must not navigate on its own.
    const {reset: resetNearby} = nearby;
    useEffect(() => {
        if (!isOpen) {
            resetNearby();
        }
    }, [isOpen, resetNearby]);
    const navItemsRef = useRef<ISearchComboboxNavItem[]>([]);
    const suggestions = usePricesSearchSuggestions({
        query,
        isOpen,
        context,
        recent: recentSearches.items,
        nearby: nearby.state,
        onPendingSubmitReady: (itemId) => navItemsRef.current.find((item) => item.id === itemId)?.onSelect()
    });
    const {model} = suggestions;

    const layerHistory = usePricesSearchLayerHistory({isOpen: isLayerOpen, onClose: () => setIsOpen(false)});
    const closePanel = () => (mode === "layer" ? layerHistory.close() : setIsOpen(false));

    const {resolveMode, isLayerViewport} = useSearchComboboxMode({
        layerMaxWidthPx: PRICES_SEARCH_LAYER_MAX_WIDTH_PX,
        // Crossing the breakpoint with the panel open closes it; the phrase stays for the next open.
        onViewportModeChange: () => {
            if (isOpen) {
                closePanel();
            }
        }
    });

    const panel = useAnchoredPanel({anchorRef: formRef, isOpen: isDropdownOpen, mode: "portal"});
    const rootRefs = useMemo(() => [formRef, panel.panelRef, layerRef], [panel.panelRef]);

    const track = (searchQuery: string, searchMethod: PricesSearchMethod) =>
        scheduleTrackingTask(() => trackPricesSearch({location, searchQuery, searchMethod}));

    // iOS raises the keyboard only for a focus() made while the tap is handled: the layer mounts synchronously here
    // and focuses its input in the same commit.
    const openLayer = () => {
        // Closing returns focus to the control that opened the layer ("Szukaj" or the hero field). Safari does not focus a
        // tapped button, so the hero field is the fallback.
        const {activeElement} = document;
        layerReturnFocusRef.current = activeElement instanceof HTMLElement && formRef.current?.contains(activeElement) ? activeElement : heroInputRef.current;
        flushSync(() => {
            setMode("layer");
            setIsOpen(true);
        });
    };

    const openPanel = (reason: SearchComboboxOpenChangeReason) => {
        if (resolveMode() === "layer") {
            // Focus may come back to the hero field when the layer closes - it must not open it again.
            if (reason !== "focus") {
                openLayer();
            }
            return;
        }

        setMode("dropdown");
        setIsOpen(true);
    };

    const onOpenChange = (nextIsOpen: boolean, reason: SearchComboboxOpenChangeReason) => {
        if (nextIsOpen) {
            if (!isOpen) {
                openPanel(reason);
            }
            return;
        }

        if (mode === "dropdown") {
            setIsOpen(false);
            return;
        }

        // Esc typed in the layer input is stopped by the combobox hook, so the layer's own Esc handling never sees it:
        // close through the history entry like ←. Tab and outside presses stay inside the full-screen layer.
        if (reason === "escape") {
            layerHistory.close();
        }
    };

    const clearQuery = () => {
        suggestions.abortAll();
        setQuery("");
    };

    function selectNearbyTarget(target: IPricesSearchNearbyTarget) {
        if (target.kind === "city") {
            saveCitySearch(target.citySlug, target.regionId);
        } else {
            saveDistrictSearch(target);
        }
        track(target.title, "manual_input");
        layerHistory.leave(target.href);
    }

    const selectLocation = (item: PricesSearchNavigableItem) => {
        // Another row picked while "Ceny w mojej okolicy" is locating: its late answer must not navigate over this one.
        resetNearby();
        const recentEntry = "recentId" in item && item.recentId ? recentSearches.items.find((entry) => entry.id === item.recentId) : undefined;

        switch (item.kind) {
            case "city":
            case "chip":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveCitySearch(item.citySlug, item.regionId);
                }
                break;
            case "district":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveDistrictSearch({regionId: item.regionId, citySlug: item.citySlug, title: item.title, href: item.href ?? "", region: item.region});
                }
                break;
            case "address":
            case "number":
                if (recentEntry) {
                    saveRecentAgain(recentEntry);
                } else {
                    saveAddressSearch(item.rcn, item.citySlug);
                }
                break;
            default:
                return;
        }

        if (item.href) {
            track(item.kind === "chip" ? item.label : item.searchLabel, getSearchMethod(item.id));
            layerHistory.leave(item.href);
        }
    };

    const selectItem = (item: PricesSearchNavigableItem) => {
        switch (item.kind) {
            case "street":
                suggestions.toggleStreet(item);
                return;
            case "showMore":
                suggestions.showMore(item);
                return;
            case "clearRecent":
                recentSearches.clear();
                return;
            case "retry":
                suggestions.retry();
                return;
            case "nearby":
                if (item.state.status === "resolved") {
                    selectNearbyTarget(item.state.target);
                } else {
                    nearby.locate();
                }
                return;
            default:
                // Enter on the auto-active number whose price is still loading (the combobox selects it directly, not through
                // onSubmitWithoutActive): wait for the price - an unavailable one hands Enter to the next row.
                if (item.id === model.autoActiveId && suggestions.isAutoActiveResolving) {
                    suggestions.markPendingSubmit();
                    return;
                }
                selectLocation(item);
        }
    };

    // Rows keep stable handlers; a selection always runs with the state of the last render. Assigned while rendering, as
    // a pending Enter may select from an effect of the suggestions hook, which runs before this component's effects.
    const selectItemRef = useRef(selectItem);
    selectItemRef.current = selectItem;

    const navItems = useMemo(
        () =>
            model.navigation.map(
                (item): ISearchComboboxNavItem => ({
                    id: item.id,
                    onSelect: () => selectItemRef.current(item),
                    isNavigation: item.isNavigation,
                    ...("recentId" in item && item.recentId ? {onRemove: () => recentSearches.remove(item.recentId as string)} : {}),
                    ...(item.kind === "street" ? {isExpanded: item.isExpanded} : {}),
                    // No data behind it: the address page would only redirect to `?adres_niedostepny=1`.
                    ...((item.kind === "number" || item.kind === "address") && item.isUnavailable ? {isDisabled: true} : {}),
                    // The browser will not ask for the location again, so selecting the row could do nothing.
                    ...(item.kind === "nearby" && item.state.status === "blocked" ? {isDisabled: true} : {})
                })
            ),
        [model, recentSearches.remove]
    );
    navItemsRef.current = navItems;

    // Enter or "Szukaj" with nothing active.
    const onSubmitWithoutActive = () => {
        if (!isOpen) {
            if (resolveMode() === "layer") {
                // The lime "Szukaj" under the field on mobile opens the layer with the current phrase.
                openLayer();
                return;
            }

            openPanel("submit");
            if (suggestions.parsed) {
                suggestions.markPendingSubmit();
            } else {
                heroInputRef.current?.focus();
            }
            return;
        }

        if (!suggestions.parsed) {
            (mode === "layer" ? layerInputRef : heroInputRef).current?.focus();
            return;
        }
        if (model.state === "error") {
            suggestions.retry();
            return;
        }
        if (model.state === "results" && model.autoActiveId !== null) {
            navItems.find((item) => item.id === model.autoActiveId)?.onSelect();
            return;
        }
        if (suggestions.isPending) {
            suggestions.markPendingSubmit();
        }
    };

    const combobox = useSearchCombobox({
        isOpen,
        onOpenChange,
        items: navItems,
        autoActiveId: model.autoActiveId,
        inputValue: query,
        onSubmitWithoutActive,
        rootRefs,
        listboxLabel: COPY.listboxLabel
    });

    // "Pokaż więcej" and a street expansion hand the keyboard position to the first newly shown row.
    const {requestedActiveId, consumeActiveRequest} = suggestions;
    const {setActiveId} = combobox;
    useEffect(() => {
        if (requestedActiveId !== null) {
            setActiveId(requestedActiveId);
            consumeActiveRequest();
        }
    }, [requestedActiveId, setActiveId, consumeActiveRequest]);

    const onClear = () => {
        clearQuery();
        combobox.reset();
        (mode === "layer" ? layerInputRef : heroInputRef).current?.focus();
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        combobox.submit();
    };

    const inputUserProps = {
        value: query,
        onChange: (event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value),
        placeholder: COPY.placeholder
    };

    // Below 1024px the hero field is only the trigger of the layer: no keyboard, announced as opening a dialog.
    const heroTriggerProps = {
        readOnly: isLayerViewport,
        "aria-haspopup": isLayerViewport ? ("dialog" as const) : undefined,
        // Below 1024px focus alone must not reach the combobox: ignoring its open request in `onOpenChange` would leave
        // the request pending, and a tap delivers focus and click in one task - the click that opens the layer would be
        // dropped. Cancelling here skips the hook's focus handling altogether.
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
            if (resolveMode() === "layer") {
                event.preventDefault();
            }
        }
    };
    const heroInputProps: InputHTMLAttributes<HTMLInputElement> & {id: string} = isLayerOpen
        ? {...inputUserProps, ...heroTriggerProps, id: heroInputId, autoComplete: "off"}
        : combobox.getInputProps({...inputUserProps, ...heroTriggerProps});
    const layerInputProps = isLayerOpen
        ? ({...combobox.getInputProps(inputUserProps), "data-testid": PRICES_SEARCH_TEST_IDS.MOBILE_INPUT} as InputHTMLAttributes<HTMLInputElement>)
        : {};

    const liveMessage = isOpen ? getLiveMessage(model) : "";
    const listProps = {
        combobox,
        onRemoveRecent: recentSearches.remove
    };

    return (
        <form ref={formRef} role="search" className={cx(formStyle, className)} onSubmit={onSubmit} data-testid={dataTestId}>
            <PricesSearchField
                inputProps={heroInputProps}
                inputRef={heroInputRef}
                label={COPY.inputLabel}
                inputDataTestId={PRICES_SEARCH_TEST_IDS.INPUT}
                controls={
                    // The mobile trigger has no controls - they live in the layer field.
                    isLayerViewport ? null : (
                        <SearchComboboxFieldControls
                            isBusy={isDropdownOpen && suggestions.isBusy}
                            showClear={query.length > 0}
                            onClear={onClear}
                            clearLabel={COPY.clearField}
                            clearDataTestId={PRICES_SEARCH_TEST_IDS.CLEAR}
                            spinnerDataTestId={PRICES_SEARCH_TEST_IDS.SPINNER}
                        />
                    )
                }
            />

            <Button type="submit" variant="filled_primary" size="big" className={buttonStyle} dataTestId={PRICES_SEARCH_TEST_IDS.SUBMIT}>
                Szukaj
            </Button>

            <SearchComboboxLiveRegion message={isLayerOpen ? "" : liveMessage} />

            <SearchComboboxDropdown
                isOpen={isDropdownOpen}
                panel={panel}
                listboxProps={combobox.getListboxProps()}
                dataTestId={PRICES_SEARCH_TEST_IDS.DROPDOWN}
            >
                <PricesSearchResults model={model} mode="dropdown" {...listProps} />
            </SearchComboboxDropdown>

            <PricesSearchLayer
                isOpen={isLayerOpen}
                onClose={layerHistory.close}
                inputProps={layerInputProps}
                inputRef={layerInputRef}
                returnFocusRef={layerReturnFocusRef}
                layerRef={layerRef}
                listboxProps={combobox.getListboxProps()}
                controls={
                    <SearchComboboxFieldControls
                        size="mobile"
                        isBusy={suggestions.isBusy}
                        showClear={query.length > 0}
                        onClear={onClear}
                        clearLabel={COPY.clearField}
                        clearDataTestId={PRICES_SEARCH_TEST_IDS.CLEAR}
                        spinnerDataTestId={PRICES_SEARCH_TEST_IDS.SPINNER}
                    />
                }
                liveRegion={<SearchComboboxLiveRegion message={isLayerOpen ? liveMessage : ""} />}
            >
                <PricesSearchResults model={model} mode="layer" {...listProps} />
            </PricesSearchLayer>
        </form>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",

    md: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 2
    }
});

const buttonStyle = css({
    flex: "0 0 auto",
    width: "100%",
    height: "5.6rem",

    md: {
        width: "20rem",
        height: "7.2rem"
    }
});
